import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { SalesDocumentType, SalesDocument, State, Complaint, IccDocument } from '@icc/b2b/shared';
import { ConfigService, TranslateService } from '@icc/shared';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map, mapTo, skipWhile, startWith, take, tap } from 'rxjs/operators';
import { getComplaintsDocument, getSalesDocument, getSalesDocumentsLoaded } from './+state/sales-documents.selectors';
import { SalesDocumentService } from './sales-document.service';
import { SyncService } from './sync.service';
import { LoaderService } from './loader.service';
import { ToastService } from '@icc/shared/helpers';
import { ComplaintService } from '@icc/complaint';

@Injectable()
export class SalesDocumentAccessGuard  {
    constructor(
        private configService: ConfigService,
        private salesDocumentService: SalesDocumentService,
        private complaintService: ComplaintService,
        private store: Store<State>,
        private router: Router,
        private syncService: SyncService,
        private loaderService: LoaderService,
        private toastService: ToastService,
        private translateService: TranslateService,
    ) {}

    async canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ) {
        const loaded = await this.isLoaded();
        if (!loaded) {
            return false;
        }
        const documentExists = await this.getDocumentExists(route.params.id);
        if (!documentExists) {
            this.loaderService.afterNoDocumentLoaded();
        }
        const document: IccDocument | boolean = await this.getDocument(route.params.id);
        if (!document) {
            return false;
        }
        if (document.isDeleted) {
            this.toastService.add({
                title: this.translateService.instant('SALES_DOCUMENT|Dokument jest usunięty'),
                body: this.translateService.instant('SALES_DOCUMENT|Dokument jest usunięty. Po zsynchronizowaniu i odświeżeniu zniknie z listy.'),
            });
        }
        let hasAccess = false;
        if(document.type === 'sales' && document?.salesDocumentTypeId && !document.isDeleted){
            switch (document.salesDocumentTypeId) {
                case SalesDocumentType.Quotation:
                    hasAccess = (
                        Number(document.sellerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany) &&
                        await this.salesDocumentService.hasAccessToQuotationOrInquiry(document)
                    );
                    break;
                case SalesDocumentType.Order:
                    hasAccess = await this.salesDocumentService.hasAccessToOrder(document);
                    break;
                case SalesDocumentType.Inquiry:
                    hasAccess = await this.salesDocumentService.hasAccessToQuotationOrInquiry(document);
                    break;
                case SalesDocumentType.ServiceOrder:
                    hasAccess = await this.salesDocumentService.hasAccessToServiceOrder(document);
                    break;
            }
        } else if (document.type === 'complaint' && !document.isDeleted) {
            hasAccess = await this.complaintService.hasAccessToComplaint(document);
        }
        if (hasAccess) {
            return true
        }

        this.loaderService.setError('NO_ACCESS');

        if (document?.type === 'sales') {
            if (document?.salesDocumentTypeId === SalesDocumentType.Order) {
                return this.router.parseUrl('/trade/offer/list/orders');
            }
            if (document?.salesDocumentTypeId === SalesDocumentType.Inquiry) {
                return this.router.parseUrl('/trade/offer/list/inquiries');
            }
            if (document?.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
                return this.router.parseUrl('/trade/offer/list/service-orders');
            }

            return this.router.parseUrl('/trade/offer/list/offers');
        } else {
            return this.router.parseUrl('/trade/complaint/list');
        }
    }

    async getDocument(id: string): Promise<IccDocument | false> {
        this.syncService.setCurrentDocumentsIds([id]);
        await this.syncService.loadDocuments([id]);
        if (id.startsWith('c-')) {
            return combineLatest([
                this.store.select(getComplaintsDocument(id)).pipe(startWith(null)),
                this.loaderService.cancel$
            ]).pipe(
                skipWhile(([document, cancel]) => !document && !cancel),
                take(1),
                map(([document, cancel]) => cancel ? false : document)
            ).toPromise() as Promise<Complaint>;
        } else {
            return combineLatest([
                this.store.select(getSalesDocument(id)).pipe(startWith(null)),
                this.loaderService.cancel$
            ]).pipe(
                skipWhile(([document, cancel]) => !document && !cancel),
                take(1),
                map(([document, cancel]) => cancel ? false : document)
            ).toPromise() as Promise<SalesDocument>;
        }
    }

    async getDocumentExists(id: string): Promise<IccDocument | false> {
        this.syncService.setCurrentDocumentsIds([id]);
        if (id.startsWith('c-')) {
            return this.store.select(getComplaintsDocument(id)).pipe(
                take(1),
            ).toPromise() as Promise<Complaint>;
        } else {
            return this.store.select(getSalesDocument(id)).pipe(
                take(1),
            ).toPromise() as Promise<SalesDocument>;
        }
    }

    async isLoaded() {
        return combineLatest([
            this.store.select(getSalesDocumentsLoaded).pipe(startWith(null)),
            this.loaderService.cancel$
        ]).pipe(
            skipWhile(([loaded, cancel]) => !loaded && !cancel),
            take(1),
            map(([loaded, cancel]) => loaded)
        ).toPromise()
    }
}
